import { actionLogTemplates, createDefaultTimelineSteps } from "./actionLogHelpers";
import { CreateActionLogData, ActionLog } from "@/shared/types/actionLog";

// This utility provides functions to automatically log task status changes
// Integration points for your existing task management hooks

export interface TaskStatusChangeContext {
  estateId: number;
  userId?: string; // Which user took this action
  taskName: string;
  oldStatus: string;
  newStatus: string;
  changedBy: "user" | "elayne" | "admin";
  metadata?: Record<string, string | number | boolean>;
}

export class TaskActionLogger {
  private static async saveActionLog(logData: CreateActionLogData): Promise<ActionLog | null> {
    const { supabase } = await import("@/shared/lib/supabase");

    try {
      const { data, error } = await supabase
        .from("action_logs")
        .insert([
          {
            ...logData,
            metadata: logData.metadata || {},
            is_visible_to_user: logData.is_visible_to_user ?? true,
          },
        ])
        .select()
        .single();

      if (error) {
        console.error("Failed to save action log:", error);
        return null;
      }

      return data;
    } catch (err) {
      console.error("Failed to save action log:", err);
      return null;
    }
  }

  static async logTaskStarted(context: TaskStatusChangeContext): Promise<void> {
    const logData = actionLogTemplates.createTaskStarted(
      context.estateId,
      context.taskName,
      context.changedBy === "user" ? "user" : "elayne",
      {
        ...context.metadata,
        previous_status: context.oldStatus,
        new_status: context.newStatus,
      },
    );

    // Add user_id to the log data
    logData.user_id = context.userId;

    await this.saveActionLog(logData);
  }

  static async logTaskCompleted(context: TaskStatusChangeContext): Promise<void> {
    const logData = actionLogTemplates.createTaskCompleted(
      context.estateId,
      context.taskName,
      context.changedBy === "user" ? "user" : "elayne",
      {
        ...context.metadata,
        previous_status: context.oldStatus,
        new_status: context.newStatus,
      },
    );

    // Add user_id to the log data
    logData.user_id = context.userId;

    await this.saveActionLog(logData);
  }

  // Generate default timeline steps when task is completed by Elayne
  static async createInitialTimeline(
    estateId: number,
    taskName: string,
    submissionDate?: string,
  ): Promise<void> {
    const defaultSteps = createDefaultTimelineSteps(estateId, taskName, submissionDate);

    for (const step of defaultSteps) {
      await this.saveActionLog(step);
    }
  }

  // Generic status change logger - logs ALL status changes
  static async logStatusChange(context: TaskStatusChangeContext): Promise<void> {
    // Create appropriate log entry based on new status
    let logData: CreateActionLogData;

    switch (context.newStatus) {
      case "not_started":
        logData = actionLogTemplates.createStatusUpdate(
          context.estateId,
          context.taskName,
          "Task reset to not started",
          new Date().toISOString().split("T")[0],
          "pending",
          {
            ...context.metadata,
            previous_status: context.oldStatus,
            new_status: context.newStatus,
          },
        );
        break;

      case "in_progress":
        // Map in_progress to pending since we removed the in_progress status
        logData = actionLogTemplates.createTaskStarted(
          context.estateId,
          context.taskName,
          context.changedBy === "user" ? "user" : "elayne",
          {
            ...context.metadata,
            previous_status: context.oldStatus,
            new_status: context.newStatus,
          },
        );
        break;

      case "complete":
        logData = actionLogTemplates.createTaskCompleted(
          context.estateId,
          context.taskName,
          "user", // Manual completion by user
          {
            ...context.metadata,
            previous_status: context.oldStatus,
            new_status: context.newStatus,
          },
        );
        break;

      case "pending_elayne":
        logData = actionLogTemplates.createStatusUpdate(
          context.estateId,
          context.taskName,
          "Elayne is processing",
          new Date().toISOString().split("T")[0],
          "pending",
          {
            ...context.metadata,
            previous_status: context.oldStatus,
            new_status: context.newStatus,
          },
        );
        break;

      case "on_it_elayne":
        logData = actionLogTemplates.createStatusUpdate(
          context.estateId,
          context.taskName,
          "Elayne is on it",
          new Date().toISOString().split("T")[0],
          "pending",
          {
            ...context.metadata,
            previous_status: context.oldStatus,
            new_status: context.newStatus,
          },
        );
        break;

      case "awaiting_confirmation_elayne":
        logData = actionLogTemplates.createStatusUpdate(
          context.estateId,
          context.taskName,
          "Elayne awaiting confirmation",
          new Date().toISOString().split("T")[0],
          "pending",
          {
            ...context.metadata,
            previous_status: context.oldStatus,
            new_status: context.newStatus,
          },
        );
        break;

      case "followed_up_elayne":
        logData = actionLogTemplates.createStatusUpdate(
          context.estateId,
          context.taskName,
          "Elayne followed up",
          new Date().toISOString().split("T")[0],
          "pending",
          {
            ...context.metadata,
            previous_status: context.oldStatus,
            new_status: context.newStatus,
          },
        );
        break;

      case "needs_info_elayne":
        logData = actionLogTemplates.createStatusUpdate(
          context.estateId,
          context.taskName,
          "Elayne needs more information",
          new Date().toISOString().split("T")[0],
          "pending",
          {
            ...context.metadata,
            previous_status: context.oldStatus,
            new_status: context.newStatus,
          },
        );
        break;

      case "completed_elayne":
        logData = actionLogTemplates.createTaskCompleted(
          context.estateId,
          context.taskName,
          "elayne", // Completed by Elayne
          {
            ...context.metadata,
            previous_status: context.oldStatus,
            new_status: context.newStatus,
          },
        );

        // Also create default timeline steps for Elayne completions
        await this.createInitialTimeline(context.estateId, context.taskName);
        break;

      default:
        // Fallback for any unknown status
        logData = actionLogTemplates.createStatusUpdate(
          context.estateId,
          context.taskName,
          `Status changed to ${context.newStatus}`,
          new Date().toISOString().split("T")[0],
          "pending",
          {
            ...context.metadata,
            previous_status: context.oldStatus,
            new_status: context.newStatus,
          },
        );
        break;
    }

    // Add user_id to the log data
    logData.user_id = context.userId;

    // Save the log
    await this.saveActionLog(logData);
  }
}

// Hook for easier integration
export function useTaskActionLogger() {
  const logStatusChange = async (context: TaskStatusChangeContext) => {
    await TaskActionLogger.logStatusChange(context);
  };

  const logCompletion = async (context: TaskStatusChangeContext) => {
    await TaskActionLogger.logTaskCompleted(context);
  };

  const logStart = async (context: TaskStatusChangeContext) => {
    await TaskActionLogger.logTaskStarted(context);
  };

  const createTimeline = async (estateId: number, taskName: string, submissionDate?: string) => {
    await TaskActionLogger.createInitialTimeline(estateId, taskName, submissionDate);
  };

  return { logStatusChange, logCompletion, logStart, createTimeline };
}

// Integration example for your useStepStatus hook:
//
// ```typescript
// export function useStepStatus(taskId: number) {
//   const { logStatusChange } = useTaskActionLogger();
//
//   const updateTaskStatus = async (newStatus: TaskStatus) => {
//     const oldStatus = currentTask.status;
//
//     // Update the task status in database
//     const updatedTask = await updateStatus(taskId, newStatus);
//
//     // Log the status change for Recent Updates
//     await logStatusChange({
//       estateId: user.estateId,
//       taskName: currentTask.name,
//       oldStatus,
//       newStatus,
//       changedBy: 'user', // or determine based on context
//     });
//
//     return updatedTask;
//   };
//
//   return { mutate: updateTaskStatus };
// }
// ```
